import React, { useEffect, useState } from "react";
import { UserPlus, Repeat, UserX } from "lucide-react";
import { useAuth } from "../lib/AuthContext";
import { api } from "../lib/api";

// Multi-account: every account signed in on this device is remembered so you
// can switch between them, add another, or jump into a basic public test account.
const KEY = "rosterly_accounts";
const read = () => { try { return JSON.parse(localStorage.getItem(KEY) || "[]") || []; } catch { return []; } };
const write = (list) => { try { localStorage.setItem(KEY, JSON.stringify(list)); } catch { /* noop */ } };

export function rememberCurrentAccount(user) {
    const token = localStorage.getItem("rosterly_token");
    if (!user?.email || !token || localStorage.getItem("rosterly_owner_token") || localStorage.getItem("rosterly_nosave") === "1") return;
    write([{ email: user.email, name: user.name || user.email, token }, ...read().filter((a) => a.email !== user.email)]);
}

// OWNER-ONLY public test account. Uses an owner token already on this device
// (signed in now, or a saved account) — the backend rejects anything else.
export async function startPublicEmulator() {
    const cur = localStorage.getItem("rosterly_token");
    const tokens = [...new Set([localStorage.getItem("rosterly_owner_token"), cur, ...read().map((a) => a.token)].filter(Boolean))];
    for (const t of tokens) {
        try {
            const { data } = await api.post("/admin/public-emulator", {}, { headers: { Authorization: `Bearer ${t}` } });
            localStorage.setItem("rosterly_owner_token", t);
            localStorage.setItem("rosterly_token", data.token);
            window.location.href = "/setup";
            return true;
        } catch { /* not an owner token */ }
    }
    return false;
}

export function AccountSwitcher({ compact = false, tiles = false }) {
    const { user, isOwner } = useAuth();
    const [list, setList] = useState(read);
    useEffect(() => { rememberCurrentAccount(user); setList(read()); }, [user]);
    const others = list.filter((a) => a.email !== user?.email);
    const switchTo = (a) => { localStorage.setItem("rosterly_token", a.token); window.location.reload(); };
    const addAnother = () => { rememberCurrentAccount(user); localStorage.removeItem("rosterly_token"); window.location.href = "/login"; };
    const forget = (email) => { const n = read().filter((a) => a.email !== email); write(n); setList(n); };
    const publicTester = () => startPublicEmulator();
    if (!user) return null;
    if (tiles) {
        const tile = "relative h-14 rounded-lg border bg-[#15161F] flex flex-col items-center justify-center gap-1 transition-colors";
        const tl = "text-[9px] font-black uppercase tracking-tight leading-none text-center px-1";
        return (
            <>
                {others.map((a) => (
                    <button key={a.email} type="button" onClick={() => switchTo(a)} data-testid={`account-switch-${a.email}`} className={`${tile} border-white/15 text-white/80`}>
                        <Repeat className="h-4 w-4 shrink-0" /><span className={`${tl} truncate max-w-full`}>{a.name}</span>
                        <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); forget(a.email); }} title="Forget this account" data-testid={`account-forget-${a.email}`} className="absolute top-0.5 right-0.5 h-5 w-5 inline-flex items-center justify-center text-white/30"><UserX className="h-3 w-3" /></span>
                    </button>
                ))}
                <button type="button" onClick={addAnother} data-testid="account-add-another" className={`${tile} border-[#22C55E]/40 text-[#22C55E]`}>
                    <UserPlus className="h-4 w-4 shrink-0" /><span className={tl}>Add Another Account</span>
                </button>
                {isOwner && (
                    <button type="button" onClick={publicTester} data-testid="account-public-emulator" className={`${tile} border-[#F6C453]/40 text-[#F6C453]`}>
                        <UserX className="h-4 w-4 shrink-0" /><span className={tl}>Public Test Account</span>
                    </button>
                )}
            </>
        );
    }
    const btn = compact
        ? "w-full h-10 px-3 rounded-lg border bg-[#15161F] flex items-center gap-2 text-[10px] font-black uppercase tracking-wide text-left"
        : "h-9 px-3 rounded-lg border flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider transition-colors hover:brightness-125";
    return (
        <div className={compact ? "flex flex-col gap-1" : "px-5 pt-5 flex flex-wrap items-center gap-2"} data-testid={compact ? "mobile-account-switcher" : "account-switcher"}>
            {others.map((a) => (
                <span key={a.email} className={compact ? "flex items-center gap-0.5" : "inline-flex items-center gap-0.5"}>
                    <button type="button" onClick={() => switchTo(a)} data-testid={`account-switch-${a.email}`} className={`${btn} border-white/15 text-white/80`}>
                        <Repeat className="h-3.5 w-3.5 shrink-0" /><span className="truncate">Switch to {a.name}</span>
                    </button>
                    <button type="button" onClick={() => forget(a.email)} title="Forget this account" data-testid={`account-forget-${a.email}`} className="h-9 w-7 inline-flex items-center justify-center text-white/30 hover:text-white"><UserX className="h-3.5 w-3.5" /></button>
                </span>
            ))}
            <button type="button" onClick={addAnother} data-testid="account-add-another" className={`${btn} border-[#22C55E]/50 text-[#22C55E]`}>
                <UserPlus className="h-3.5 w-3.5" /> Add another account
            </button>
            {isOwner && (
                <button type="button" onClick={publicTester} data-testid="account-public-emulator" className={`${btn} border-[#F6C453]/50 text-[#F6C453]`}>
                    <UserX className="h-3.5 w-3.5" /> Public test account
                </button>
            )}
        </div>
    );
}
